import { CalendarDays, Check, Clock3, MapPin, Star, Trophy, X } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";

import { Avatar } from "../components/Avatar";
import { GameFormatPoll } from "../components/GameFormatPoll";
import { PageHeader } from "../components/PageHeader";
import { useAuth } from "../context/AuthContext";
import api, { apiError } from "../lib/api";
import "./HomeArenaPageV2.css";

function largeDate(value) {
  const date = value ? new Date(`${value}T12:00:00`) : null;
  if (!date || Number.isNaN(date.getTime())) return value ? value.slice(0, 3).toUpperCase() : "—";
  return String(date.getDate()).padStart(2, "0");
}

const positions = ["forward", "mid-left", "mid-right", "back-left", "back-mid", "back-right", "keeper"];

export default function HomeArenaPageV2() {
  const { user } = useAuth();
  const matchQuery = useQuery({ queryKey: ["current-match"], queryFn: () => api.get("/matches/current").then((result) => result.data.match) });
  const playersQuery = useQuery({ queryKey: ["players"], queryFn: () => api.get("/users").then((result) => result.data) });
  const match = matchQuery.data;
  const championIds = match?.champion_ids?.length ? match.champion_ids : (match?.champion_id ? [match.champion_id] : []);
  const champions = championIds.map((id) => (playersQuery.data || []).find((player) => player.id === id)).filter(Boolean);
  const confirmed = match?.confirmed?.includes(user?.id);
  const togglePresence = async () => {
    try {
      const { data } = await api.put("/matches/current/confirm");
      toast.success(data.waiting ? "Você entrou na lista de espera." : data.confirmed ? "Presença confirmada!" : "Presença cancelada.");
      matchQuery.refetch();
    } catch (error) {
      toast.error(apiError(error));
    }
  };
  if (matchQuery.isLoading) {
    return (
      <>
        <PageHeader title="Pelada" subtitle="Dos Perninhas FC" />
        <section className="arena-page" data-testid="home-page">
          <div className="arena-empty" data-testid="home-match-loading">
            <CalendarDays size={34} />
            <h2>Carregando a pelada</h2>
            <p>Organizando as informações da próxima rodada.</p>
          </div>
        </section>
      </>
    );
  }
  if (!match) {
    return (
      <>
        <PageHeader title="Pelada" subtitle="Dos Perninhas FC" />
        <section className="arena-page" data-testid="home-page">
          <GameFormatPoll />
          <div className="arena-empty">
            <CalendarDays size={34} />
            <h2>Nenhuma pelada marcada</h2>
            <p>A próxima rodada aparecerá aqui.</p>
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      <PageHeader title="Pelada" subtitle="Dos Perninhas FC" />
      <section className="arena-page" data-testid="home-page">
        <GameFormatPoll />
        <article className="arena-match-card" data-testid="next-match-card">
          <div className="arena-header">
            <span>PRÓXIMA PELADA</span>
            <strong>{match.num_teams}×{match.players_per_team}</strong>
          </div>
          <div className="arena-date-row">
            <div className="arena-date">
              <strong data-testid="arena-date-day">{largeDate(match.data)}</strong>
            </div>
            <div className="arena-details">
              <p data-testid="match-time"><Clock3 size={17} />{match.horario}</p>
              <p data-testid="match-location"><MapPin size={17} />{match.local}</p>
              <small data-testid="confirmed-count">
                {match.confirmed.length} de {match.vagas} vagas mensais
              </small>
            </div>
          </div>
          <button
            className={`arena-presence ${confirmed ? "confirmed" : ""}`}
            onClick={togglePresence}
            data-testid="toggle-presence-button"
          >
            {confirmed ? <><Check size={18} />Presença confirmada</> : <><X size={18} />Confirmar presença</>}
          </button>
        </article>
        <section className="arena-stats">
          <div><span>CONFIRMADOS</span><strong>{match.confirmed.length}</strong></div>
          <div><span>LISTA DE ESPERA</span><strong>{match.waiting?.length || 0}</strong></div>
          <div><span>VAGAS</span><strong>{match.vagas}</strong></div>
        </section>
        <article className="champions-field-card" data-testid="champions-week-field">
          <header>
            <div><Trophy size={18} /><span>CAMPEÕES DA SEMANA</span></div>
            <small>{champions.length}/7 escolhidos</small>
          </header>
          <div className="champions-pitch">
            {champions.map((player, index) => (
              <div className={`field-champion ${positions[index]}`} key={player.id} data-testid={`week-champion-${player.id}`}>
                <Avatar user={player} size="md" />
                <strong>{player.name.split(" ")[0]}</strong>
                <small><Star size={10} fill="currentColor" /> {player.rating.toFixed(1)}</small>
              </div>
            ))}
            {!champions.length && (
              <div className="field-empty">
                <Trophy size={24} />
                <span>O admin escolhe até 7 campeões na aba Jogadores.</span>
              </div>
            )}
          </div>
        </article>
      </section>
    </>
  );
}